//@ts-nocheck
//@ts-ignore
/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useStore } from '../../store/store';
import { useNav } from '../../hooks/nav';
import { ReactComponent as BackIcon } from '../../assets/images/arrowLeft.svg';
import { ReactComponent as CheckWhiteIcon } from '../../assets/images/CheckWhite.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/checkEmailICon.svg';
import Analytics from '@web-solutions/module-analytics';
import { t } from '@web-solutions/module-localization';
import Button from 'src/onboarding/components/ui/button/Button';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';
import { Email, domains } from '@smastrom/react-email-autocomplete';
import '../email/style.scss';
import styles from './style.module.scss';
import getLocale from 'src/localization/get-locale';
import { validateEmail } from 'src/onboarding/helpers/validateEmail';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import { buildPurchaseUserData } from 'src/onboarding/helpers/buildPurchaseUserData';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import {
  PAYMENT_SYSTEM_PROJECT,
  clearCheckoutSessionId,
  submitCheckoutManualEmail,
} from 'src/onboarding/helpers/checkoutApi';

// Post-payment email collection page for the Stripe deferred-email flow.
// The user has already paid as an anonymous user (created on site open with
// email = null). Here we attach their email to that user via
// `/user/update-by-token/{userId}`. Unlike the standard `email` page this page
// does NOT re-init remote config or run `processEmail` — the config is already
// loaded and the purchase is already made; we only need to save the email.
function Page() {
  const email = useStore((state) => state.email);
  const setEmail = useStore((state) => state.setEmail);
  const userId = useStore((state) => state.userId);
  const setPage = useStore((state) => state.setPage);
  const checkoutSessionId = useStore((state) => state.checkoutSessionId);
  const setCheckoutSessionId = useStore((state) => state.setCheckoutSessionId);
  const pageAnswers = useStore((state) => state.pageAnswers);
  const imageUrl = useStore((state) => state.imageUrl);
  const { onNext, onPrev, currentIndex } = useNav();
  const locale = getLocale();
  const { apiBaseUrl } = getEnvUrl();
  const { sendEthnicity, routeGroups: flow, collectEmailAfterPayment, paymentSystem } = useRemoteConfig();

  const [errorMessage, setErrorMessage] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const baseList = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'icloud.com',
    'aol.com',
  ];

  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  useEffect(() => {
    const preventScroll = (e) => {
      const dropdown = document.querySelector('.custom-email-dropdown');
      if (!dropdown || !dropdown.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (isDropdownOpen || keyboardOpen) {
      document.body.classList.add('no-scroll');
      document.body.addEventListener('touchmove', preventScroll, { passive: false });
    } else {
      document.body.classList.remove('no-scroll');
      document.body.removeEventListener('touchmove', preventScroll);
    }

    return () => {
      document.body.classList.remove('no-scroll');
      document.body.removeEventListener('touchmove', preventScroll);
    };
  }, [isDropdownOpen, keyboardOpen]);

  useEffect(() => {
    const input = document.getElementById('email-field');
    if (!input || !isIOS()) return;

    let resizeTimeout: number | null = null;

    const updateButtonPosition = (keyboardHeight: number) => {
      const buttonElement = document.querySelector('.next-wrapper');
      const buttonBg = document.querySelector('.button-white-bg');

      const bottomValue = keyboardHeight > 100 ? `${keyboardHeight}px` : '0px';

      if (buttonElement) buttonElement.style.bottom = bottomValue;
      if (buttonBg) buttonBg.style.bottom = bottomValue;
    };

    const handleViewportResize = () => {
      if (!window.visualViewport) return;

      const keyboardHeight = window.innerHeight - window.visualViewport.height;
      const keyboardNowOpen = keyboardHeight > 150;

      setKeyboardOpen(keyboardNowOpen);
      updateButtonPosition(keyboardHeight);
    };

    const onFocus = () => {
      setKeyboardOpen(true);
      handleViewportResize();
      requestAnimationFrame(() => {
        setTimeout(() => handleViewportResize(), 200);
      });
    };

    const onBlur = () => {
      setKeyboardOpen(false);
      updateButtonPosition(0);
    };

    const onResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleViewportResize, 50);
    };

    input.addEventListener('focus', onFocus);
    input.addEventListener('blur', onBlur);
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
      window.visualViewport?.removeEventListener('resize', onResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, []);

  useEffect(() => {
    if (!email) {
      setErrorMessage('');
      setIsValid(false);
    } else if (!validateEmail(email)) {
      setErrorMessage(t('onboarding.email.errorMessage'));
      setIsValid(false);
    } else {
      setErrorMessage('');
      setIsValid(true);
    }
  }, [email]);

  const handleSubmit = useCallback(
    async (value) => {
      if (!isValid || isSubmitting) return;

      setIsSubmitting(true);

      // With `collectEmailAfterPayment` on, CompleteRegistration is already fired
      // at payment success (onSuccess on the paywall), so don't fire it again here.
      if (!collectEmailAfterPayment) {
        // @ts-ignore
        window.fbq?.('track', 'CompleteRegistration');
        // @ts-ignore
        window.ttq?.track?.('CompleteRegistration');
      }
      Analytics.setUserProperty('email', value);
      Analytics.trackEvent('user', 'info', { email: value });

      setEmail(value);

      // Hosted Stripe Checkout branch (requiresEmail:true): Stripe didn't collect
      // an email, so attach it to the session via /payments/checkout/manual-email.
      // The backend then sends the report; the profile/subscription already came
      // from the Checkout webhook, so there's no update-by-token here.
      if (checkoutSessionId) {
        try {
          await submitCheckoutManualEmail(checkoutSessionId, value, PAYMENT_SYSTEM_PROJECT);
          Analytics.trackEvent('checkout-manual-email', 'success');
        } catch (error) {
          Analytics.trackEvent('checkout-manual-email', 'error');
          console.error('Error submitting checkout manual email:', error);
        } finally {
          clearCheckoutSessionId();
          setCheckoutSessionId(null);
        }

        setIsSubmitting(false);
        onNext();
        return;
      }

      const currentUserId = userId || useStore.getState().userId;

      if (currentUserId) {
        try {
          // Card deferred-email flow: the paywall's onSuccess does NOT send
          // update-by-token for card, so this page sends the full post-purchase
          // payload (profile + subscription + the entered email) — identical to
          // what onSuccess sends for Apple Pay.
          const payload = buildPurchaseUserData({
            email: value,
            store: useStore.getState(),
            flow,
            sendEthnicity,
            paymentSystem,
          });

          const response = await axios.put(
            `${apiBaseUrl}/user/update-by-token/${currentUserId}`,
            payload
          );

          Analytics.trackEvent('email-after-payment', 'update-success');

          const { refreshToken, accessToken } = response.data || {};
          if (accessToken) {
            useStore.getState().setAuthTokens({
              refreshToken,
              accessToken,
              userId: currentUserId,
            });
          }
        } catch (error) {
          Analytics.trackEvent('email-after-payment', 'update-error');
          console.error('Error updating user email:', error);
        }
      }

      setIsSubmitting(false);
      onNext();
    },
    [apiBaseUrl, checkoutSessionId, collectEmailAfterPayment, imageUrl, isSubmitting, isValid, onNext, pageAnswers, paymentSystem, sendEthnicity, setCheckoutSessionId, setEmail, userId]
  );

  const getPaddingLeft = (email: string | undefined, locale: string): number => {
    if (email?.length >= 1) return 0;

    switch (locale) {
      case 'en':
      case 'tr':
        return 80;
      default:
        return 80;
    }
  };

  return (
    <div className={`page ${styles.afterPaymentPage}`}>
      <div className='wrapper-info'>
        <Title tag='h2'>{t('onboarding.emailAfterPayment.title')}</Title>
        <Text size='m' medium className='description'>
          {t('onboarding.emailAfterPayment.subTitle')}
        </Text>
      </div>

   

      <div className='controls email'>
        <Email
          id='email-field'
          value={email}
          onChange={setEmail}
          placeholder={t('onboarding.email.placeholder')}
          autoFocus
          baseList={baseList}
          refineList={domains}
          classNames={{
            wrapper: 'custom-email-wrapper',
            dropdown: `custom-email-dropdown ${keyboardOpen ? 'keyboard-open' : ''}`,
            input: 'custom-email-input',
            suggestion: 'custom-email-suggestion',
            domain: 'custom-email-domain',
          }}
          style={{
            textAlign: (email?.length || 0) >= 1 ? 'center' : 'left',
            caretColor: 'black',
            paddingLeft: getPaddingLeft(email, locale),
          }}
        />
        <Caption size='m' className='error-message' style={{ margin: 0 }}>
          {errorMessage}
        </Caption>
        <span className='info'>
          <span className='top-info'>{t('onboarding.email.topInfo')}</span>
          <span className='bottom-info'>
            <a onClick={() => setPage('privacy-policy')}>{t('onboarding.email.privacyPolicy')}</a>
            <span className='bottom-info-devider'>&</span>
            <a onClick={() => setPage('terms-of-use')}>{t('onboarding.email.TermofUse')}</a>
          </span>
        </span>
      </div>
      <div className={styles.benefits}>
        <div className={styles.benefit}>
          <CheckIcon />
          <span>{t('onboarding.emailAfterPayment.benefit1')}</span>
        </div>
        <div className={styles.benefit}>
          <CheckIcon />
          <span>{t('onboarding.emailAfterPayment.benefit2')}</span>
        </div>
        <div className={styles.benefit}>
          <CheckIcon />
          <span>{t('onboarding.emailAfterPayment.benefit3')}</span>
        </div>
      </div>
      <div className={`button-white-bg ${keyboardOpen && isIOS() ? 'keyboard-open' : ''}`}></div>
      <div className={`next-wrapper ${keyboardOpen && isIOS() ? 'keyboard-open' : ''}`}>
        <Button
          className={!isValid || isSubmitting ? 'disabled' : ''}
          title={t('onboarding.emailAfterPayment.continue')}
          onClick={() => {
            if (!isValid || isSubmitting) return;
            handleSubmit(email);
            Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
              page: 'email-after-payment',
              answer: email,
            });
          }}
        />
      </div>
    </div>
  );
}

export default Page;
